import * as React from 'react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { ScrollArea } from './scroll-area';

// ============================================
// TYPES
// ============================================

export interface MentionUser {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
}

export interface MentionTextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
    users: MentionUser[];
    value: string;
    onChange: (value: string) => void;
    onMentionsChange?: (mentionedUserIds: string[]) => void;
}

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// Get the local part of an email (before @)
function getEmailLocalPart(email: string): string {
    return email.split('@')[0] || email;
}

// Parse mentions from text and return array of user IDs
// Mentions are in format @email_local_part (e.g., @john.doe for john.doe@example.com)
export function parseMentionsFromText(text: string, users: MentionUser[]): string[] {
    // Match @mention where mention can include letters, numbers, dots, hyphens, underscores (email local part format)
    const mentionPattern = /@([\w.-]+)(?=\s|$|[^\w.-])/g;
    const mentionedIds: string[] = [];
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
        const mentionEmailLocal = match[1]?.toLowerCase() ?? '';
        const user = users.find(
            (u) => getEmailLocalPart(u.email).toLowerCase() === mentionEmailLocal
        );
        if (user && !mentionedIds.includes(user.id)) {
            mentionedIds.push(user.id);
        }
    }

    return mentionedIds;
}

// ============================================
// COMPONENT
// ============================================

const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
    ({ className, users, value, onChange, onMentionsChange, disabled, ...props }, ref) => {
        const [showSuggestions, setShowSuggestions] = useState(false);
        const [suggestionFilter, setSuggestionFilter] = useState('');
        const [selectedIndex, setSelectedIndex] = useState(0);
        const [cursorPosition, setCursorPosition] = useState(0);
        const [mentionStartIndex, setMentionStartIndex] = useState(-1);

        const textareaRef = useRef<HTMLTextAreaElement>(null);
        const suggestionsRef = useRef<HTMLDivElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);

        // Combine forwarded ref with internal ref
        React.useImperativeHandle(ref, () => textareaRef.current!);

        // Filter users based on current input
        const filteredUsers = useMemo(() => {
            if (!suggestionFilter) return users;
            const filter = suggestionFilter.toLowerCase();
            return users.filter(
                (user) =>
                    user.name.toLowerCase().includes(filter) ||
                    user.email.toLowerCase().includes(filter)
            );
        }, [users, suggestionFilter]);

        // Reset selected index when filtered users change
        useEffect(() => {
            setSelectedIndex(0);
        }, [filteredUsers]);

        // Extract mentioned user IDs and notify parent
        useEffect(() => {
            if (onMentionsChange) {
                const mentionedIds = parseMentionsFromText(value, users);
                onMentionsChange(mentionedIds);
            }
        }, [value, users, onMentionsChange]);

        // Handle text change
        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.target.value;
                const newCursorPosition = e.target.selectionStart || 0;
                setCursorPosition(newCursorPosition);
                onChange(newValue);

                // Check if we should show suggestions
                const textBeforeCursor = newValue.slice(0, newCursorPosition);
                const lastAtIndex = textBeforeCursor.lastIndexOf('@');

                if (lastAtIndex >= 0) {
                    // Check if @ is at start or after a space/newline
                    const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
                    if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
                        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
                        // Only show if there's no space after the partial mention
                        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                            setMentionStartIndex(lastAtIndex);
                            setSuggestionFilter(textAfterAt);
                            setShowSuggestions(true);
                            return;
                        }
                    }
                }

                setShowSuggestions(false);
                setMentionStartIndex(-1);
                setSuggestionFilter('');
            },
            [onChange]
        );

        // Handle keyboard navigation
        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (!showSuggestions || filteredUsers.length === 0) {
                    // Pass through to parent handler if provided
                    if (props.onKeyDown) {
                        props.onKeyDown(e);
                    }
                    return;
                }

                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault();
                        setSelectedIndex((prev) =>
                            prev < filteredUsers.length - 1 ? prev + 1 : 0
                        );
                        break;
                    case 'ArrowUp':
                        e.preventDefault();
                        setSelectedIndex((prev) =>
                            prev > 0 ? prev - 1 : filteredUsers.length - 1
                        );
                        break;
                    case 'Enter':
                        if (!e.shiftKey && showSuggestions && filteredUsers[selectedIndex]) {
                            e.preventDefault();
                            selectUser(filteredUsers[selectedIndex]);
                        } else if (props.onKeyDown) {
                            props.onKeyDown(e);
                        }
                        break;
                    case 'Escape':
                        e.preventDefault();
                        setShowSuggestions(false);
                        break;
                    case 'Tab':
                        if (showSuggestions && filteredUsers.length > 0 && filteredUsers[selectedIndex]) {
                            e.preventDefault();
                            selectUser(filteredUsers[selectedIndex]);
                        }
                        break;
                    default:
                        if (props.onKeyDown) {
                            props.onKeyDown(e);
                        }
                }
            },
            [showSuggestions, filteredUsers, selectedIndex, props.onKeyDown]
        );

        // Select a user from suggestions
        const selectUser = useCallback(
            (user: MentionUser) => {
                if (!textareaRef.current || mentionStartIndex < 0) return;

                const beforeMention = value.slice(0, mentionStartIndex);
                const afterCursor = value.slice(cursorPosition);
                // Use email local part as the mention identifier for uniqueness
                const emailLocalPart = user.email.split('@')[0] || user.email;
                const newValue = `${beforeMention}@${emailLocalPart} ${afterCursor}`;

                onChange(newValue);
                setShowSuggestions(false);
                setMentionStartIndex(-1);
                setSuggestionFilter('');

                // Focus and set cursor position after the inserted mention
                setTimeout(() => {
                    if (textareaRef.current) {
                        const newCursorPos = beforeMention.length + emailLocalPart.length + 2; // +2 for @ and space
                        textareaRef.current.focus();
                        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                    }
                }, 0);
            },
            [value, mentionStartIndex, cursorPosition, onChange]
        );

        // Close suggestions when clicking outside
        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (
                    containerRef.current &&
                    !containerRef.current.contains(e.target as Node)
                ) {
                    setShowSuggestions(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        // Scroll selected item into view
        useEffect(() => {
            if (showSuggestions && suggestionsRef.current) {
                const selectedElement = suggestionsRef.current.querySelector(
                    `[data-index="${selectedIndex}"]`
                );
                selectedElement?.scrollIntoView({ block: 'nearest' });
            }
        }, [selectedIndex, showSuggestions]);

        return (
            <div ref={containerRef} className="relative">
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    className={cn(
                        'flex min-h-[80px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-foreground-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        className
                    )}
                    {...props}
                />

                {/* Suggestions dropdown */}
                {showSuggestions && filteredUsers.length > 0 && (
                    <div
                        ref={suggestionsRef}
                        className="absolute left-0 right-0 z-50 mt-1 max-h-[200px] overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
                    >
                        <ScrollArea className="max-h-[200px]">
                            <div className="p-1">
                                {filteredUsers.map((user, index) => (
                                    <button
                                        key={user.id}
                                        type="button"
                                        data-index={index}
                                        className={cn(
                                            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                                            index === selectedIndex
                                                ? 'bg-primary/10 text-primary'
                                                : 'hover:bg-surface-hover'
                                        )}
                                        onClick={() => selectUser(user)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        <Avatar className="h-6 w-6">
                                            <AvatarImage src={user.avatarUrl || undefined} />
                                            <AvatarFallback className="bg-primary/20 text-primary text-xs">
                                                {getInitials(user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{user.name}</div>
                                            <div className="text-xs text-foreground-muted truncate">
                                                {user.email}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                )}

                {/* Empty state */}
                {showSuggestions && filteredUsers.length === 0 && suggestionFilter && (
                    <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg border border-border bg-surface p-3 text-center text-sm text-foreground-muted shadow-lg">
                        No users found matching "{suggestionFilter}"
                    </div>
                )}
            </div>
        );
    }
);

MentionTextarea.displayName = 'MentionTextarea';

export { MentionTextarea };
